/** Сегмент plain-text после разбиения по URL. */
export type PlainTextSegment =
  | { type: 'text'; value: string }
  | { type: 'link'; value: string; href: string };

const URL_REGEXP = /(?:https?:\/\/|www\.)[\w\-._~:/?#[\]@!$&'()*+,;=%]+/gi;

const TRAILING_PUNCTUATION_REGEXP = /[)\],;:.!?]+$/;

/** Убирает завершающую пунктуацию, не входящую в URL (например «…com).»). */
export function trimUrlTrailingPunctuation(raw: string): { url: string; trailing: string } {
  let url = raw;
  let trailing = '';

  while (url.endsWith(')')) {
    const openCount = (url.match(/\(/g) ?? []).length;
    const closeCount = (url.match(/\)/g) ?? []).length;
    if (closeCount <= openCount) break;
    trailing = `)${trailing}`;
    url = url.slice(0, -1);
  }

  const punctMatch = url.match(TRAILING_PUNCTUATION_REGEXP);
  if (punctMatch) {
    trailing = `${punctMatch[0]}${trailing}`;
    url = url.slice(0, -punctMatch[0].length);
  }

  return { url, trailing };
}

export function toLinkHref(url: string): string {
  return url.toLowerCase().startsWith('www.') ? `https://${url}` : url;
}

/** Разбивает строку на текстовые фрагменты и URL без React-зависимостей. */
export function splitPlainTextByUrls(text: string): PlainTextSegment[] {
  if (text === '') return [];

  const segments: PlainTextSegment[] = [];
  let lastIndex = 0;
  const re = new RegExp(URL_REGEXP.source, URL_REGEXP.flags);

  for (const match of text.matchAll(re)) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, start) });
    }

    const raw = match[0];
    const { url, trailing } = trimUrlTrailingPunctuation(raw);

    if (url.length > 0) {
      segments.push({ type: 'link', value: url, href: toLinkHref(url) });
    } else {
      segments.push({ type: 'text', value: raw });
    }

    if (trailing.length > 0) {
      segments.push({ type: 'text', value: trailing });
    }

    lastIndex = start + raw.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ type: 'text', value: text }];
}

export function plainTextHasUrl(text: string): boolean {
  return splitPlainTextByUrls(text).some((segment) => segment.type === 'link');
}
